import { FC, FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useJobPostingService } from '../../services/useJobPostingService';
import { JobPostingDto } from '../../types/jobPostingDto';
import './addjobpostingpage.css';

const AddJobPostingPage: FC = () => {
    const { createJobPosting } = useJobPostingService();
    const [title, setTitle] = useState('');
    const [company, setCompany] = useState('');
    const [source, setSource] = useState('');
    const [sourceUrl, setSourceUrl] = useState('');
    const [postedDate, setPostedDate] = useState(new Date().toISOString().slice(0, 10));
    const [jobDescription, setJobDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState<JobPostingDto | null>(null);

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (!title.trim() || jobDescription.trim().length < 50 || saving) return;
        setSaving(true);
        setError(null);
        setSaved(null);
        try {
            setSaved(await createJobPosting({
                title: title.trim(),
                company: company.trim(),
                source: source.trim(),
                sourceUrl: sourceUrl.trim(),
                postedDate: new Date(`${postedDate}T12:00:00`).toISOString(),
                jobDescription: jobDescription.trim(),
            }));
        } catch (err: any) {
            const data = err?.response?.data;
            setError(data?.message ?? data?.detail ?? err?.message ?? 'Unable to save this job posting.');
        } finally {
            setSaving(false);
        }
    };

    const reset = () => {
        setTitle('');
        setCompany('');
        setSource('');
        setSourceUrl('');
        setPostedDate(new Date().toISOString().slice(0, 10));
        setJobDescription('');
        setSaved(null);
        setError(null);
    };

    return (
        <main className="jp-page">
            <header className="jp-header">
                <div className="jp-eyebrow">Reusable hiring context</div>
                <h1>Add Job Posting</h1>
                <p>Save a role once, then use it independently in Recruiter Review and Candidate Comparison.</p>
            </header>

            <form className="jp-panel" onSubmit={submit}>
                <div className="jp-grid">
                    <label>
                        <span>Job title *</span>
                        <input value={title} onChange={event => setTitle(event.target.value)} required />
                    </label>
                    <label>
                        <span>Company</span>
                        <input value={company} onChange={event => setCompany(event.target.value)} />
                    </label>
                    <label>
                        <span>Source</span>
                        <input value={source} onChange={event => setSource(event.target.value)} placeholder="LinkedIn, company site…" />
                    </label>
                    <label>
                        <span>Posted date</span>
                        <input type="date" value={postedDate} onChange={event => setPostedDate(event.target.value)} />
                    </label>
                    <label className="jp-wide">
                        <span>Source URL</span>
                        <input type="url" value={sourceUrl} onChange={event => setSourceUrl(event.target.value)} placeholder="https://…" />
                    </label>
                    <label className="jp-wide">
                        <span>Job description *</span>
                        <textarea value={jobDescription} onChange={event => setJobDescription(event.target.value)} placeholder="Paste the complete job description…" required />
                        <small>{jobDescription.trim().length} characters · minimum 50</small>
                    </label>
                </div>

                {error && <div className="jp-error">{error}</div>}
                {saved && (
                    <div className="jp-success">
                        <div><strong>{saved.title}</strong> is ready to use.</div>
                        <div className="jp-success-links">
                            <Link to="/recruiterreview">Run Recruiter Review</Link>
                            <Link to="/candidatecomparison">Compare Candidates</Link>
                        </div>
                    </div>
                )}

                <div className="jp-actions">
                    <button type="button" className="jp-secondary" onClick={reset}>Clear</button>
                    <button type="submit" className="jp-primary" disabled={saving || !title.trim() || jobDescription.trim().length < 50}>
                        {saving ? 'Saving…' : 'Save Job Posting'}
                    </button>
                </div>
            </form>
        </main>
    );
};

export default AddJobPostingPage;
